/**
 * WebSocket Integration for Real-time Updates
 *
 * Core WebSocket functionality for real-time asset tracking using Socket.IO.
 * Integrates with simplified query system and error handling.
 */

import { io, Socket } from 'socket.io-client';
import { queryClient } from './query-client';
import { queryKeys } from './query-keys';
import { createNetworkError, reportError } from './error-handling';
import { isDevelopment } from './featureFlags';
import { getWebSocketUrl } from './apiConfig';
import type { DomainEvent, PositionEvent, AlertEvent } from './domain-events';

// WebSocket connection state
interface WebSocketState {
  socket: Socket | null;
  isConnected: boolean;
  isAuthenticated: boolean;
  factoryId: string | null;
  subscriptions: Set<string>;
  reconnectAttempts: number;
}

class WebSocketManager {
  private state: WebSocketState = {
    socket: null,
    isConnected: false,
    isAuthenticated: false,
    factoryId: null,
    subscriptions: new Set(),
    reconnectAttempts: 0,
  };

  // Backoff for reconnects after the server drops us. Socket.IO does not
  // auto-reconnect on 'io server disconnect' (e.g. auth rejected an expired
  // token), so we retry ourselves; capped so a real access problem can't hammer.
  private readonly serverReconnectMaxDelayMs = 30_000;
  private serverReconnectAttempts = 0;
  private serverReconnectTimer: ReturnType<typeof setTimeout> | null = null;

  // Reconnect as soon as the browser regains network instead of waiting out
  // the current backoff delay.
  private readonly handleOnline = (): void => {
    if (this.state.socket && !this.state.isConnected) {
      this.state.socket.connect();
    }
  };

  // Stored token getter — refreshed on each (re)connect to handle token expiry
  private tokenGetter: (() => Promise<string | null>) | null = null;

  // In-flight connect promise — coalesces concurrent connect() calls so
  // React StrictMode / focus events / rapid navigation can't open duplicate
  // sockets before the first one transitions to isConnected.
  private connectInFlight: Promise<void> | null = null;

  // Connect to WebSocket server for a specific factory
  async connect(factoryId: string, tokenGetter?: () => Promise<string | null>): Promise<void> {
    if (this.state.isConnected && this.state.factoryId === factoryId) {
      return;
    }

    // Coalesce concurrent connect() calls for the same factory.
    if (this.connectInFlight && this.state.factoryId === factoryId) {
      return this.connectInFlight;
    }

    // Close existing connection if different factory
    if (this.state.socket && this.state.factoryId !== factoryId) {
      this.disconnect();
    }

    if (tokenGetter) {
      this.tokenGetter = tokenGetter;
    }

    this.state.factoryId = factoryId;

    this.connectInFlight = new Promise<void>((resolve, reject) => {
      const wsBaseUrl = getWebSocketUrl();

      try {
        // Create Socket.IO connection
        this.state.socket = io(wsBaseUrl, {
          transports: ['websocket', 'polling'],
          reconnection: true,
          // Never give up: a kiosk screen must survive network drops of any length.
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
        });

        // Handle connection
        this.state.socket.on('connect', async () => {
          this.state.isConnected = true;
          this.state.reconnectAttempts = 0;

          if (isDevelopment()) {
            console.log('Socket.IO connected');
          }

          // Authenticate with backend - send token AND tenantId (like REST API)
          if (this.tokenGetter) {
            const token = await this.tokenGetter();
            const tenantId = localStorage.getItem('selected_tenant_id');

            if (token && tenantId) {
              this.state.socket?.emit('authenticate', { token, tenantId });
            } else {
              const authError = createNetworkError(
                !token ? 'No auth token available' : 'No tenant selected'
              );
              reportError(authError, { factoryId });
              reject(authError);
            }
          }
        });

        // Handle authentication success
        this.state.socket.on('authenticated', (data: { userId: string; tenantId: string }) => {
          this.state.isAuthenticated = true;
          this.serverReconnectAttempts = 0;

          if (isDevelopment()) {
            console.log('Socket authenticated:', data);
          }

          // Subscribe to factory updates
          if (this.state.factoryId) {
            this.state.socket?.emit('subscribe:factory', this.state.factoryId);

            this.emitEvent('WEBSOCKET_CONNECTED', {
              type: 'WEBSOCKET_CONNECTED',
              factoryId: this.state.factoryId,
              timestamp: Date.now(),
            });

            if (isDevelopment()) {
              console.log(`Subscribed to factory: ${this.state.factoryId}`);
            }
          }

          resolve();
        });

        // Handle position updates from backend
        this.state.socket.on('position:update', (positions: unknown[]) => {
          if (isDevelopment()) {
            console.log('Position update received:', positions.length, 'positions');
          }

          // Invalidate real-time positions to trigger refetch
          if (this.state.factoryId) {
            queryClient.invalidateQueries({
              queryKey: queryKeys.positions.realtime(this.state.factoryId),
            });
          }

          // Emit as domain event for listeners
          this.emitEvent('POSITIONS_BATCH_RECEIVED', {
            type: 'POSITIONS_BATCH_RECEIVED',
            factoryId: this.state.factoryId || '',
            assetId: '', // Batch event - no single assetId
            position: { assetId: '', x: 0, y: 0, timestamp: Date.now() }, // Required field
            positions: positions as Array<{
              assetId: string;
              x: number;
              y: number;
              timestamp: number;
            }>,
            timestamp: Date.now(),
          } as PositionEvent);
        });

        // Handle alerts from backend
        this.state.socket.on('alert:triggered', (alert: unknown) => {
          if (isDevelopment()) {
            console.log('Alert received:', alert);
          }

          // Invalidate alerts to trigger refetch
          if (this.state.factoryId) {
            queryClient.invalidateQueries({
              queryKey: queryKeys.alerts.lists(this.state.factoryId),
            });
          }

          // Emit as domain event
          this.emitEvent('ALERT_TRIGGERED', {
            type: 'ALERT_TRIGGERED',
            factoryId: this.state.factoryId || '',
            alertId: (alert as { id?: string })?.id || '',
            alert,
            timestamp: Date.now(),
          } as AlertEvent);
        });

        // Handle alert resolved events from backend
        this.state.socket.on('alert:resolved', (alertId: unknown) => {
          if (isDevelopment()) {
            console.log('Alert resolved:', alertId);
          }

          // Invalidate alerts to trigger refetch
          if (this.state.factoryId) {
            queryClient.invalidateQueries({
              queryKey: queryKeys.alerts.lists(this.state.factoryId),
            });
          }

          // Emit as domain event
          this.emitEvent('ALERT_RESOLVED', {
            type: 'ALERT_RESOLVED',
            factoryId: this.state.factoryId || '',
            alertId: String(alertId ?? ''),
            timestamp: Date.now(),
          } as AlertEvent);
        });

        // Handle disconnection
        this.state.socket.on('disconnect', (reason: string) => {
          this.state.isConnected = false;
          this.state.isAuthenticated = false;

          if (isDevelopment()) {
            console.log('Socket.IO disconnected:', reason);
          }

          if (reason === 'io server disconnect') {
            this.scheduleServerReconnect();
          }

          this.emitEvent('WEBSOCKET_DISCONNECTED', {
            type: 'WEBSOCKET_DISCONNECTED',
            factoryId: this.state.factoryId || '',
            timestamp: Date.now(),
          });
        });

        // Handle reconnection
        this.state.socket.on('reconnect', (attemptNumber: number) => {
          this.state.isConnected = true;
          this.state.reconnectAttempts = 0;

          if (isDevelopment()) {
            console.log('Socket.IO reconnected after', attemptNumber, 'attempts');
          }

          // Re-authenticate after reconnect
          if (this.tokenGetter) {
            this.tokenGetter()
              .then(token => {
                const tenantId = localStorage.getItem('selected_tenant_id');
                if (token && tenantId) {
                  this.state.socket?.emit('authenticate', { token, tenantId });
                }
              })
              .catch(err => {
                if (isDevelopment()) {
                  console.error('Failed to re-authenticate after reconnect:', err);
                }
              });
          }

          this.emitEvent('WEBSOCKET_RECONNECTED', {
            type: 'WEBSOCKET_RECONNECTED',
            factoryId: this.state.factoryId || '',
            timestamp: Date.now(),
          });
        });

        // Handle connection errors
        this.state.socket.on('connect_error', (error: Error) => {
          this.state.reconnectAttempts += 1;

          const wsError = createNetworkError('WebSocket connection failed');
          reportError(wsError, { factoryId, error: error.message });

          this.emitEvent('ERROR_OCCURRED', {
            type: 'ERROR_OCCURRED',
            factoryId: this.state.factoryId || '',
            timestamp: Date.now(),
          });

          if (this.state.reconnectAttempts === 1) {
            reject(wsError);
          }
        });

        // Handle authentication errors
        this.state.socket.on('error', (data: { message: string }) => {
          const authError = createNetworkError(data.message || 'WebSocket error');
          reportError(authError, { factoryId });
          reject(authError);
        });
        window.addEventListener('online', this.handleOnline);
      } catch (error) {
        const wsError = createNetworkError('Failed to create WebSocket connection');
        reportError(wsError, { factoryId, error });
        reject(wsError);
      }
    });

    const inFlight = this.connectInFlight;
    inFlight.finally(() => {
      if (this.connectInFlight === inFlight) {
        this.connectInFlight = null;
      }
    });
    return inFlight;
  }

  private scheduleServerReconnect(): void {
    if (this.serverReconnectTimer) {
      return;
    }

    const delay = Math.min(
      1000 * 2 ** this.serverReconnectAttempts,
      this.serverReconnectMaxDelayMs
    );
    this.serverReconnectAttempts += 1;

    this.serverReconnectTimer = setTimeout(() => {
      this.serverReconnectTimer = null;
      this.state.socket?.connect();
    }, delay);
  }

  // Disconnect from WebSocket server
  disconnect(): void {
    window.removeEventListener('online', this.handleOnline);
    if (this.serverReconnectTimer) {
      clearTimeout(this.serverReconnectTimer);
      this.serverReconnectTimer = null;
    }
    this.serverReconnectAttempts = 0;

    if (this.state.socket) {
      // Unsubscribe from factory if needed
      if (this.state.factoryId) {
        this.state.socket.emit('unsubscribe:factory', this.state.factoryId);
      }

      this.state.socket.disconnect();
      this.state.socket = null;
    }

    this.state.isConnected = false;
    this.state.isAuthenticated = false;
    this.state.factoryId = null;
    this.state.subscriptions.clear();
    this.connectInFlight = null;
  }

  // Subscribe to real-time updates (compatibility method - Socket.IO handles this automatically)
  subscribe(subscriptionType: 'POSITIONS' | 'ALERTS' | 'ASSETS' | 'ZONES'): void {
    if (!this.state.isConnected || !this.state.factoryId) {
      if (isDevelopment()) {
        console.warn('Cannot subscribe: WebSocket not connected');
      }
      return;
    }

    const subscriptionKey = `${this.state.factoryId}:${subscriptionType}`;
    this.state.subscriptions.add(subscriptionKey);

    if (isDevelopment()) {
      console.log('Subscribed to:', subscriptionType);
    }
  }

  // Unsubscribe from data updates (compatibility method)
  unsubscribe(subscriptionType: 'POSITIONS' | 'ALERTS' | 'ASSETS' | 'ZONES'): void {
    if (!this.state.factoryId) {
      return;
    }

    const subscriptionKey = `${this.state.factoryId}:${subscriptionType}`;
    this.state.subscriptions.delete(subscriptionKey);
  }

  // Event listeners for domain events
  private eventListeners: Map<string, Set<(event: DomainEvent) => void>> = new Map();

  addEventListener(eventType: string, handler: (event: DomainEvent) => void): () => void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, new Set());
    }
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      listeners.add(handler);
    }

    // Return cleanup function
    return (): void => {
      const listeners = this.eventListeners.get(eventType);
      if (listeners) {
        listeners.delete(handler);
        if (listeners.size === 0) {
          this.eventListeners.delete(eventType);
        }
      }
    };
  }

  removeEventListener(eventType: string, handler: (event: DomainEvent) => void): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      listeners.delete(handler);
      if (listeners.size === 0) {
        this.eventListeners.delete(eventType);
      }
    }
  }

  private emitEvent(eventType: string, event: DomainEvent): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners && listeners.size > 0) {
      listeners.forEach(handler => {
        try {
          handler(event);
        } catch (error) {
          const eventError = createNetworkError(`Error in event listener for ${eventType}`);
          reportError(eventError, { eventType, error });
        }
      });
    }
  }

  // Get connection status
  getStatus(): {
    isConnected: boolean;
    factoryId: string | null;
    subscriptions: string[];
    reconnectAttempts: number;
  } {
    return {
      isConnected: this.state.isConnected && this.state.isAuthenticated,
      factoryId: this.state.factoryId,
      subscriptions: Array.from(this.state.subscriptions),
      reconnectAttempts: this.state.reconnectAttempts,
    };
  }
}

// Create and export WebSocket manager instance
export const webSocketManager = new WebSocketManager();

// React hook for WebSocket status
export const useWebSocketStatus = (): {
  isConnected: boolean;
  factoryId: string | null;
  subscriptions: string[];
  reconnectAttempts: number;
} => webSocketManager.getStatus();

// Convenience functions for factory updates
export const subscribeToFactoryUpdates = async (
  factoryId: string,
  tokenGetter?: () => Promise<string | null>
): Promise<void> => {
  await webSocketManager.connect(factoryId, tokenGetter);
  webSocketManager.subscribe('POSITIONS');
  webSocketManager.subscribe('ALERTS');
  webSocketManager.subscribe('ASSETS');
};

export const unsubscribeFromFactoryUpdates = (): void => {
  webSocketManager.disconnect();
};
